import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { homePathForRole } from "@/lib/roleRouter";
import { KeyRound, ChevronLeft, MailCheck } from "lucide-react";
import TempCodeDialog from "@/components/TempCodeDialog";

/**
 * iter70c · Public vendor back-office (shared-team passkey) login.
 * iter71b · Added the Forgot Passkey tab. Since the vendor's password reset
 * link (already implemented) delivers the vendor to `/reset-password` and
 * eventually into their portal, the "Forgot Passkey" flow just reuses the
 * existing `/auth/forgot-password` endpoint — after logging in via the
 * reset link the vendor can rotate the passkey from Settings.
 */
const PASSCODE_MIN = 5;
const PASSCODE_MAX = 10;

export default function VendorBackOfficeLoginPage() {
  const navigate = useNavigate();
  const { user, setUser, refreshCapabilities } = useAuth();
  const [tab, setTab] = useState("passkey");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [forgotErr, setForgotErr] = useState("");
  const [tempCodeOpen, setTempCodeOpen] = useState(false);

  useEffect(() => {
    if (user && user.role === "vendor") {
      navigate(homePathForRole("vendor"), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const code = passcode.trim();
    if (code.length < PASSCODE_MIN || code.length > PASSCODE_MAX) {
      setError(`Passkey must be ${PASSCODE_MIN}–${PASSCODE_MAX} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/vendor/back-office/login", { passcode: code });
      setUser(data.user);
      try { await refreshCapabilities?.(); } catch { /* non-fatal */ }
      navigate(homePathForRole("vendor"), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPasscode("");
    } finally {
      setSubmitting(false);
    }
  };

  const requestForgot = async (e) => {
    e.preventDefault();
    setForgotErr("");
    if (!forgotEmail.trim()) {
      setForgotErr("Enter the vendor account email address.");
      return;
    }
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotDone(true);
    } catch (err) {
      setForgotErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setForgotBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f1a14] text-[#F9F8F4] flex flex-col"
         data-testid="vendor-back-office-page">
      <header className="border-b border-[#1A422D] px-6 sm:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.png" alt="MCC" className="h-8 w-auto" />
          <span className="font-serif text-lg">My Crest Club</span>
        </Link>
        <Link to="/login" className="text-xs uppercase tracking-[0.24em] text-[#D9C68A] hover:text-[#F9F8F4] inline-flex items-center gap-1.5"
              data-testid="vendor-back-office-login-link">
          <ChevronLeft size={13} /> Email + Password Sign In
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-md">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D9C68A]/40 text-[#D9C68A] text-[10px] uppercase tracking-[0.28em]">
              <KeyRound size={12} /> Vendor Team Passkey
            </div>
            <h1 className="font-serif text-4xl mt-6">Vendor Back Office</h1>
            <p className="text-sm text-[#B7B7A8] mt-3 leading-relaxed">
              Type the shared passkey your company set from the vendor portal, or reset it via email if forgotten.
            </p>
          </div>

          {/* Tabs */}
          <div className="mt-8 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.24em]">
            <button
              type="button"
              onClick={() => setTab("passkey")}
              data-testid="vbo-tab-passkey"
              className={`px-4 py-2 border transition-colors ${tab === "passkey"
                ? "bg-[#D9C68A] text-[#0f1a14] border-[#D9C68A]"
                : "text-[#D9C68A]/85 border-[#D9C68A]/40 hover:border-[#D9C68A]"}`}
            >Sign In</button>
            <button
              type="button"
              onClick={() => { setTab("forgot"); setForgotDone(false); setForgotErr(""); }}
              data-testid="vbo-tab-forgot"
              className={`px-4 py-2 border transition-colors ${tab === "forgot"
                ? "bg-[#D9C68A] text-[#0f1a14] border-[#D9C68A]"
                : "text-[#D9C68A]/85 border-[#D9C68A]/40 hover:border-[#D9C68A]"}`}
            >Forgot Passkey</button>
          </div>

          {tab === "passkey" && (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="passcode" className="text-[10px] uppercase tracking-[0.28em] text-[#D9C68A] block mb-2">
                  Team Passkey
                </label>
                <input
                  id="passcode"
                  type="text"
                  inputMode="text"
                  autoFocus
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value.replace(/\s/g, ""))}
                  maxLength={PASSCODE_MAX}
                  data-testid="vendor-back-office-passcode"
                  className="w-full bg-transparent border-b border-[#D9C68A]/40 focus:border-[#D9C68A] outline-none py-3 text-center text-3xl tracking-[0.5em] font-mono uppercase"
                  placeholder="•••••"
                />
              </div>
              {error && (
                <div className="text-sm text-[#f1c2c2] border border-[#a33] bg-[#3a1414] px-4 py-2"
                     data-testid="vendor-back-office-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !passcode}
                data-testid="vendor-back-office-submit"
                className="w-full bg-[#D9C68A] text-[#0f1a14] px-5 py-3 text-xs uppercase tracking-[0.28em] font-semibold hover:bg-[#F9F8F4] disabled:opacity-50"
              >
                {submitting ? "Signing in…" : "Enter Vendor Portal"}
              </button>
            </form>
          )}

          {tab === "forgot" && (
            <form onSubmit={requestForgot} className="mt-8 space-y-5" data-testid="vbo-forgot-form">
              {forgotDone ? (
                <div className="p-5 border border-[#D9C68A]/40 bg-[#1A422D]/40 text-sm text-[#F9F8F4] flex items-start gap-2"
                     data-testid="vbo-forgot-done">
                  <MailCheck size={16} className="text-[#D9C68A] mt-0.5 flex-shrink-0" />
                  <div>
                    If <strong>{forgotEmail}</strong> is on file, we&rsquo;ve sent a password reset link.
                    Follow it to sign in, then rotate your Team Passkey from{" "}
                    <span className="text-[#D9C68A]">Vendor Portal → Settings → Shared Team Passkey</span>.
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#B7B7A8] leading-relaxed">
                    We&rsquo;ll email a password-reset link to your vendor account. After signing in you can rotate
                    the shared Team Passkey from Settings.
                  </p>
                  <div>
                    <label htmlFor="vbo-forgot-email" className="text-[10px] uppercase tracking-[0.28em] text-[#D9C68A] block mb-2">
                      Vendor Account Email
                    </label>
                    <input
                      id="vbo-forgot-email"
                      type="email"
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      data-testid="vbo-forgot-email"
                      className="w-full bg-transparent border-b border-[#D9C68A]/40 focus:border-[#D9C68A] outline-none py-3 text-base"
                      placeholder="sales@yourcompany.com"
                    />
                  </div>
                  {forgotErr && (
                    <div className="text-sm text-[#f1c2c2] border border-[#a33] bg-[#3a1414] px-4 py-2"
                         data-testid="vbo-forgot-error">{forgotErr}</div>
                  )}
                  <button
                    type="submit"
                    disabled={forgotBusy || !forgotEmail}
                    data-testid="vbo-forgot-submit"
                    className="w-full bg-[#D9C68A] text-[#0f1a14] px-5 py-3 text-xs uppercase tracking-[0.28em] font-semibold hover:bg-[#F9F8F4] disabled:opacity-50"
                  >
                    {forgotBusy ? "Sending…" : "Send Reset Link"}
                  </button>
                </>
              )}
            </form>
          )}

          <div className="mt-10 text-center text-[11px] text-[#7A9080] tracking-wide">
            No passkey yet? A vendor admin can set one from
            <span className="text-[#D9C68A]"> Vendor Portal → Settings → Shared Team Passkey</span>.
          </div>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setTempCodeOpen(true)}
              data-testid="vbo-temp-code-trigger"
              className="text-[11px] uppercase tracking-[0.22em] text-[#D9C68A]/85 hover:text-[#D9C68A] underline-offset-4 hover:underline"
            >
              Send Temporary Login Code Instead
            </button>
          </div>
        </div>
      </main>
      <TempCodeDialog
        open={tempCodeOpen}
        onClose={() => setTempCodeOpen(false)}
        roleLabel="Vendor Portal"
        onSuccess={(u) => {
          setUser(u);
          setTempCodeOpen(false);
          navigate(homePathForRole(u.role || "vendor"), { replace: true });
        }}
      />
    </div>
  );
}
